import { ButtonsGroup } from 'components/shared/ButtonsGroup/ButtonsGroup';
import { DocumentsSelector } from 'components/shared/DocumentsSelector/DocumentsSelector';
import type { EmailServiceDocument } from 'models/emailService/EmailServiceDocument';
import type { EmailServiceRecipient } from 'models/emailService/EmailServiceRecipient';
import { FC, useCallback, useMemo } from 'react';
import { ModalBody } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { logAnalytics } from 'shared/middlewares/analytics/logger';
import { emailServiceDocumentsChanged, emailServiceRecipientChanged } from 'state/emailService/emailService.actions';
import {
  getEmailServiceAvailableDocuments,
  getEmailServiceEmailDocuments,
  getEmailServiceEmailRecipient,
  shouldShowLoadingSkeleton,
} from 'state/emailService/emailService.selectors';
import { isFeatureEnabled } from 'state/selectors/common';
import type { AppState } from 'state/state.model';

import { EmailServiceLoadingSkeleton } from './EmailServiceLoadingSkeleton';
import { EmailServicePreviewForm } from './EmailServicePreviewForm';

const recipientOptions: EmailServiceRecipient[] = ['Client', 'Me'];

// TODO: Unit tests
export const EmailServiceForm: FC = () => {
  const dispatch = useDispatch();

  const availableDocuments = useSelector(getEmailServiceAvailableDocuments);
  const recipient = useSelector(getEmailServiceEmailRecipient);
  const selectedDocuments = useSelector(getEmailServiceEmailDocuments);
  const recipientValue = useMemo(() => [recipient], [recipient]);
  const canEmailServiceSendToClient = useSelector((state: AppState) =>
    isFeatureEnabled(state, 'emailServiceSendToClientHK'),
  );
  const showLoadingSkeleton = useSelector(shouldShowLoadingSkeleton);

  const onRecipientChanged = useCallback(
    (value: readonly EmailServiceRecipient[]) => {
      dispatch(emailServiceRecipientChanged(value[0]));
      logAnalyticsForRecipient(value[0]);
    },
    [dispatch],
  );

  const onDocumentsChanged = useCallback(
    (value: readonly EmailServiceDocument[]) => {
      dispatch(emailServiceDocumentsChanged(value));
    },
    [dispatch],
  );

  return (
    <ModalBody>
      {showLoadingSkeleton ? (
        <EmailServiceLoadingSkeleton numPreviewGroups={recipient === 'Client' ? 4 : 3} />
      ) : (
        <>
          <div className="form-group mb-3">
            <ButtonsGroup
              className="w-100 btn-group-lg"
              items={recipientOptions}
              disabledItems={canEmailServiceSendToClient ? [] : ['Client']}
              selectedItems={recipientValue}
              translationPrefix="emailService.fields.recipient.options"
              isRadio
              onSelect={onRecipientChanged}
            />
          </div>
          <EmailServicePreviewForm />
          <DocumentsSelector
            id="email-service-documents"
            value={selectedDocuments}
            options={availableDocuments}
            onChange={onDocumentsChanged}
          />
        </>
      )}
    </ModalBody>
  );
};

EmailServiceForm.displayName = 'EmailServiceForm';

function logAnalyticsForRecipient(recipient: EmailServiceRecipient) {
  switch (recipient) {
    case 'Client':
      logAnalytics.trackEvent('Email service', 'Change recipient', 'Send to client');
      break;
    case 'Me':
      logAnalytics.trackEvent('Email service', 'Change recipient', 'Forward to me');
      break;
    default:
      break;
  }
}
